import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

/** Interface that describes a single step. */
export interface Step {
    /** Unique step key. */
    key: string;
    /** Render function - takes any number of arguments. */
    Component: (...args: any[]) => ReactNode;
}

type UseStepperPayload<S extends Step, R extends readonly Step[]> = [
    S["key"], // It will be literal (not string) but exact value - '1', '2', ...etc.
    R // Type of passed steps array.
];
interface UseStepperActions<S extends Step> {
    /**
     * A function that checks whether the step key is equal
     * to the specified key - if so, it assigns the appropriate
     * type (type-guard).
     */
    is: <S extends Step, K extends S["key"]>(
        step: S,
        key: K
    ) => step is Extract<
        S,
        {
            key: K;
        }
    >;
    /** Goes to first step. */
    first: () => Step;
    /** Goes to last step. */
    last: () => Step;
    /** Sets previous step. */
    previous: () => Step;
    /** Sets next step. */
    next: () => Step;
    /** Sets the step by a specific key. */
    set: <K extends S["key"]>(key: K) => void;
}
type UseStepperReturn<S extends Step> = [S, UseStepperActions<S>];

const cannotFindStepError = "Cannot find step";

const is = <S extends Step, K extends S["key"]>(
    step: S, // Type of step - it will be union from array of steps.
    key: K // The exact key value - it will be literal.
): step is Extract<S, { key: K }> => step.key === key;

const useStepper = <S extends Step, R extends readonly Step[]>(...payload: UseStepperPayload<S, R>): UseStepperReturn<S> => {
    const [initialKey, steps] = payload;
    const [key, setKey] = useState(initialKey);

    const set = useCallback(<K extends S["key"]>(key: K): void => {
        setKey(key);
    }, []);

    const first = useCallback((): Step => {
        const firstStep = steps[0];
        setKey(firstStep.key);
        return firstStep;
    }, [steps]);

    const last = useCallback((): Step => {
        const lastStep = steps[steps.length - 1];
        setKey(lastStep.key);
        return lastStep;
    }, [steps]);

    const previous = useCallback((): Step => {
        const idx = steps.findIndex((s) => s.key === key) - 1;
        const previousIdx = idx < 0 ? steps.length - 1 : idx;
        const previousStep = steps[previousIdx];
        setKey(previousStep.key);
        return previousStep;
    }, [key, steps]);

    const next = useCallback((): Step => {
        const idx = steps.findIndex((s) => s.key === key) + 1;
        const nextIdx = idx >= steps.length ? 0 : idx;
        const nextStep = steps[nextIdx];
        setKey(nextStep.key);
        return nextStep;
    }, [key, steps]);

    const step = useMemo(() => {
        const foundStep = steps.find((s) => s.key === key);

        if (!foundStep) {
            throw new Error(cannotFindStepError);
        }

        return foundStep as S;
    }, [key, steps]);

    return [step, { is, set, previous, next, first, last }];
};

export { useStepper };
